// mobile-cockpit.tsx — version mobile du cockpit RemoteWS.
// Navigation : pager HORIZONTAL Accueil↔Machine, pager VERTICAL par écran
// (Accueil = Connectés/Favoris/Tous · Machine = Info + 1 pane par outil).
// Gestes pointer (h/v + distinction scroll de contenu), points indicateurs et
// chevrons de bord. Câblé sur l'état du cockpit (App) via props ; aperçu/santé
// sessions live via useWs().

import { createSignal, createMemo, createEffect, onMount, onCleanup, For, Show, type JSX } from "solid-js";
import { useWs } from "./shared/ws";
import { useNotify } from "./shared/notify";
import { useConfirm } from "./shared/confirm-dialog";
import { PwaInstallButton } from "./shared/pwa-install";
import { asWsOutgoing, envelope, isWsEnvelope } from "./shared/ws-protocol";
import type { HostData, Section, OpenMachine, ClientNode } from "./app";
import "./mobile-cockpit.css";

interface RailCatLite {
  key: string;
  ic: string;
  n: string;
  tools: Section[];
}

type MobileControlMode = "ws" | "rdp";

export interface MobileCockpitProps {
  openMachines: OpenMachine[];
  favoriteHosts: HostData[];
  clientTree: ClientNode[];
  pinnedHostIds: string[];
  togglePin: (hostId: string) => void;
  activeMachine: OpenMachine | null;
  connectEnrolled: (h: HostData) => void;
  switchMachine: (key: string) => void;
  disconnectMachine: (key: string) => void | Promise<void>;
  section: string;
  activate: (id: string) => void;
  closeSection: (id: string) => void;
  /** Sections visitées hors « info » (= onglets d'outils de la machine active). */
  toolTabSections: Section[];
  /** Outils disponibles, groupés par catégorie (bottom-sheet). */
  toolCategories: RailCatLite[];
  sessionHost: HostData | null | undefined;
  effectiveHostOnline: boolean;
  onOpenControl: (mode?: MobileControlMode) => void;
  /** Monte le panneau réel d'une section (control/panel/settings). */
  renderToolContent: (s: Section, visible?: boolean) => JSX.Element;
}

const INFO_PANE: Section = { id: "info", label: "Info", kind: "info" };

// Libellés des 3 écrans verticaux du hub (Connectés / Favoris / Tous les hosts).
const HUB_TABS: { label: string }[] = [{ label: "Connectés" }, { label: "Favoris" }, { label: "Tous les hosts" }];

function initials(s: string): string {
  return (
    s
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
}
function hostSub(h: HostData): string {
  return `${h.clientLabel || "Sans client"} › ${h.siteLabel || "—"}`;
}

export function MobileCockpit(props: MobileCockpitProps): JSX.Element {
  const [screen, setScreen] = createSignal<"hub" | "machine">("hub");
  const [hubV, setHubV] = createSignal(0);
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const [cachedToolPanes, setCachedToolPanes] = createSignal<Section[]>([]);
  let rootRef: HTMLDivElement | undefined;
  let viewportRaf = 0;
  let cachedMachineKey: string | null = null;

  createEffect(() => {
    const machineKey = props.activeMachine?.key ?? null;
    const nextTabs = props.toolTabSections;
    if (machineKey !== cachedMachineKey) {
      cachedMachineKey = machineKey;
      setCachedToolPanes(nextTabs);
      return;
    }
    if (nextTabs.length === 0) return;
    setCachedToolPanes((prev) => {
      const nextIds = new Set(nextTabs.map((s) => s.id));
      return [...nextTabs, ...prev.filter((s) => !nextIds.has(s.id))];
    });
  });

  // Panes de la machine active : Info + chaque outil ouvert.
  const machinePanes = createMemo<Section[]>(() => [INFO_PANE, ...cachedToolPanes()]);
  // Index vertical de la machine, dérivé de la section active.
  const machineVIndex = createMemo<number>(() => {
    if (props.section === "info") return 0;
    const i = cachedToolPanes().findIndex((s) => s.id === props.section);
    return i < 0 ? 0 : i + 1;
  });

  // Connexion d'un hôte → bascule sur l'écran machine.
  function openHost(h: HostData) {
    if (!h.online) return;
    props.connectEnrolled(h);
    setScreen("machine");
  }
  function switchTo(m: OpenMachine) {
    props.switchMachine(m.key);
    setScreen("machine");
  }
  function closeToolPane(id: string) {
    setCachedToolPanes((prev) => prev.filter((s) => s.id !== id));
    props.closeSection(id);
  }
  function openRdp() {
    const rdpTool = props.toolCategories.flatMap((c) => c.tools).find((t) => t.id === "panel:rdp");
    if (rdpTool) {
      props.activate(rdpTool.id);
      setScreen("machine");
      return;
    }
    props.onOpenControl("rdp");
  }

  // ── Peinture des pagers (transforms) ──────────────────────────────────
  const htrack = () => rootRef?.querySelector<HTMLElement>(".htrack") ?? null;
  function activeVtrack(): HTMLElement | null {
    const hs = rootRef?.querySelectorAll<HTMLElement>(".hscreen");
    if (!hs) return null;
    const screenEl = hs[screen() === "hub" ? 0 : 1];
    return screenEl?.querySelector<HTMLElement>(".vtrack") ?? null;
  }
  function paintH() {
    const t = htrack();
    if (t) t.style.transform = `translateX(${screen() === "hub" ? 0 : -100}%)`;
  }
  function paintHubV() {
    const hubVt = rootRef?.querySelectorAll<HTMLElement>(".hscreen")[0]?.querySelector<HTMLElement>(".vtrack");
    if (!hubVt) return;
    // B-TOOLS-1 : clamme l'index au nombre réel de panneaux montés pour éviter
    // un transform pointant vers un panneau inexistant (écran blanc).
    const n = hubVt.children.length;
    const idx = Math.max(0, Math.min(hubV(), Math.max(0, n - 1)));
    hubVt.style.transform = `translateY(${-idx * 100}%)`;
  }
  function paintMachineV() {
    const mVt = rootRef?.querySelectorAll<HTMLElement>(".hscreen")[1]?.querySelector<HTMLElement>(".vtrack");
    if (!mVt) return;
    // B-TOOLS-1 : même garde-fou côté machine. Pendant les transitions
    // d'outils, machineVIndex() peut dépasser le nombre de panneaux montés
    // (cachedToolPanes mute de façon async) → on clamme au DOM réel.
    const n = mVt.children.length;
    const idx = Math.max(0, Math.min(machineVIndex(), Math.max(0, n - 1)));
    mVt.style.transform = `translateY(${-idx * 100}%)`;
  }
  function repaintPager() {
    paintH();
    paintHubV();
    paintMachineV();
  }
  function syncViewport() {
    const root = rootRef;
    if (!root) return;
    const visualHeight = window.visualViewport?.height;
    const height = visualHeight && visualHeight > 0 ? visualHeight : window.innerHeight;
    root.style.setProperty("--mc-vh", `${Math.round(height)}px`);
    if (viewportRaf) cancelAnimationFrame(viewportRaf);
    viewportRaf = requestAnimationFrame(() => {
      viewportRaf = 0;
      if (!dragging) repaintPager();
    });
  }
  // Effets : recale les transforms quand l'état change (hors drag).
  createEffect(() => {
    screen();
    paintH();
  });
  createEffect(() => {
    // hub vertical
    hubV();
    paintHubV();
  });
  createEffect(() => {
    // machine vertical
    machineVIndex();
    machinePanes();
    paintMachineV();
  });

  // Amorce « peek » au montage (découvrabilité du swipe vertical) + listener
  // capture qui avale le clic émis à la fin d'un swipe (évite d'ouvrir un host
  // quand on relâche un geste).
  onMount(() => {
    syncViewport();
    const vt = rootRef?.querySelector<HTMLElement>(".hscreen .vtrack");
    if (vt) {
      vt.classList.add("peek");
      setTimeout(() => vt.classList.remove("peek"), 1500);
    }
    const root = rootRef;
    if (root) {
      const onCap = (e: MouseEvent) => {
        if (swallow) {
          e.stopPropagation();
          swallow = false;
        }
      };
      root.addEventListener("click", onCap, true);
      root.addEventListener("touchstart", onTouchStartBlock, { passive: false });
      root.addEventListener("touchmove", onTouchMoveBlock, { passive: false });
      const resizeObs = new ResizeObserver(syncViewport);
      resizeObs.observe(root);
      window.addEventListener("resize", syncViewport);
      window.addEventListener("orientationchange", syncViewport);
      window.visualViewport?.addEventListener("resize", syncViewport);
      window.visualViewport?.addEventListener("scroll", syncViewport);
      onCleanup(() => {
        root.removeEventListener("click", onCap, true);
        root.removeEventListener("touchstart", onTouchStartBlock);
        root.removeEventListener("touchmove", onTouchMoveBlock);
        resizeObs.disconnect();
        window.removeEventListener("resize", syncViewport);
        window.removeEventListener("orientationchange", syncViewport);
        window.visualViewport?.removeEventListener("resize", syncViewport);
        window.visualViewport?.removeEventListener("scroll", syncViewport);
        if (viewportRaf) cancelAnimationFrame(viewportRaf);
      });
    }
  });

  // ── Moteur de gestes (pointer) ────────────────────────────────────────
  // Sur écran tactile, le changement Accueil↔Machine reste volontairement
  // attaché aux boutons de bord : iOS réserve le swipe horizontal à l'historique
  // navigateur. Le pointer garde le scroll et la pagination verticale.
  const RESIST = 0.32; // facteur de résistance au bord
  const FLICK = 0.45; // px/ms — vitesse minimale pour valider un « flick »
  const activePointers = new Map<number, { x: number; y: number }>();
  let x0 = 0,
    y0 = 0,
    ax: "h" | "vpage" | "vscroll" | null = null,
    dragging = false,
    vt: HTMLElement | null = null,
    vIdx = 0,
    vh = 0,
    nPanes = 1,
    pane: HTMLElement | null = null,
    startScroll = 0,
    touchBlockX = 0,
    touchBlockY = 0,
    lastX = 0,
    lastY = 0,
    lastT = 0,
    velX = 0,
    velY = 0,
    swallow = false;

  function centroid() {
    let x = 0;
    let y = 0;
    for (const p of activePointers.values()) {
      x += p.x;
      y += p.y;
    }
    const n = Math.max(1, activePointers.size);
    return { x: x / n, y: y / n };
  }

  function armGesture(origin: { x: number; y: number }, axis: typeof ax = null) {
    dragging = true;
    ax = axis;
    x0 = lastX = origin.x;
    y0 = lastY = origin.y;
    lastT = performance.now();
    velX = velY = 0;
    vt = activeVtrack();
    vIdx = screen() === "hub" ? hubV() : machineVIndex();
    nPanes = screen() === "hub" ? 3 : machinePanes().length;
    vh = vt?.parentElement?.clientHeight ?? 1;
    pane = (vt?.children[vIdx] as HTMLElement) ?? null;
    startScroll = pane?.scrollTop ?? 0;
  }

  function armSwallow() {
    swallow = true;
    setTimeout(() => (swallow = false), 90);
  }

  function touchCentroid(e: TouchEvent) {
    let x = 0;
    let y = 0;
    for (const t of Array.from(e.touches)) {
      x += t.clientX;
      y += t.clientY;
    }
    const n = Math.max(1, e.touches.length);
    return { x: x / n, y: y / n };
  }

  function onTouchStartBlock(e: TouchEvent) {
    if (e.touches.length < 2) return;
    const c = touchCentroid(e);
    touchBlockX = c.x;
    touchBlockY = c.y;
  }

  function onTouchMoveBlock(e: TouchEvent) {
    if (e.touches.length < 2 || sheetOpen()) return;
    const c = touchCentroid(e);
    const dx = c.x - touchBlockX;
    const dy = c.y - touchBlockY;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.1) {
      e.preventDefault();
    }
  }

  function onPointerDown(e: PointerEvent) {
    const tgt = e.target as HTMLElement;
    // B-TOOLS-2 : les cartes hosts/machines (.mcard) et lignes hosts (.hostrow)
    // doivent réagir au tap natif (onClick). Sans cette exemption, le
    // pointerdown de la racine peut armer un geste qui avale ensuite le clic.
    if (tgt.closest(".sheet,.scrim,.vdots,.hedge,button,input,textarea,select,a,.connect,.star,.mcard,.hostrow,.chip"))
      return;
    if (sheetOpen()) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (e.pointerType === "touch" && activePointers.size < 2) {
      armGesture({ x: e.clientX, y: e.clientY }, "vscroll");
    } else {
      armGesture(e.pointerType === "touch" ? centroid() : { x: e.clientX, y: e.clientY });
    }
    try {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging) return;
    if (activePointers.has(e.pointerId)) activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const origin = e.pointerType === "touch" && activePointers.size > 1 ? centroid() : { x: e.clientX, y: e.clientY };
    const dx = origin.x - x0,
      dy = origin.y - y0;
    const dt = Math.max(1, e.timeStamp - lastT);
    velX = (origin.x - lastX) / dt;
    velY = (origin.y - lastY) / dt;
    lastX = origin.x;
    lastY = origin.y;
    lastT = e.timeStamp;
    if (e.pointerType === "touch" && activePointers.size < 2 && ax !== "vscroll") {
      ax = "vscroll";
    }
    if (!ax) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      // axe dominant clair (ratio 1.2) pour éviter les bascules hésitantes.
      if (Math.abs(dx) > Math.abs(dy) * 1.28) {
        if (e.pointerType === "touch") {
          e.preventDefault();
          ax = "vscroll";
          return;
        }
        ax = "h";
        const t = htrack();
        if (t) t.style.transition = "none";
        rootRef?.classList.add("dragging");
      } else {
        const atTop = startScroll <= 0;
        const atBot = pane ? startScroll + pane.clientHeight >= pane.scrollHeight - 2 : true;
        const canPage = nPanes > 1 && ((dy > 0 && atTop) || (dy < 0 && atBot));
        if (canPage) {
          ax = "vpage";
          if (vt) vt.style.transition = "none";
          rootRef?.classList.add("dragging");
        } else {
          ax = "vscroll";
        }
      }
    }
    if (ax === "h") {
      e.preventDefault();
      // résistance : Accueil (idx 0) ne déborde pas à droite, Machine (idx 1) pas à gauche.
      const over = (screen() === "hub" && dx > 0) || (screen() === "machine" && dx < 0);
      const d = over ? dx * RESIST : dx;
      const t = htrack();
      if (t) t.style.transform = `translateX(calc(${screen() === "hub" ? 0 : -100}% + ${d}px))`;
    } else if (ax === "vpage") {
      e.preventDefault();
      const over = (vIdx === 0 && dy > 0) || (vIdx >= nPanes - 1 && dy < 0);
      const d = over ? dy * RESIST : dy;
      if (vt) vt.style.transform = `translateY(calc(${-vIdx * 100}% + ${d}px))`;
    } else if (ax === "vscroll" && pane) {
      pane.scrollTop = startScroll - dy;
    }
  }

  function onPointerUp(e: PointerEvent) {
    const endOrigin =
      e.pointerType === "touch" && activePointers.size > 1 ? centroid() : { x: e.clientX, y: e.clientY };
    activePointers.delete(e.pointerId);
    if (!dragging) return;
    dragging = false;
    rootRef?.classList.remove("dragging");
    const dx = endOrigin.x - x0,
      dy = endOrigin.y - y0;
    const w = rootRef?.clientWidth ?? 1;
    if (ax === "h") {
      const t = htrack();
      if (t) t.style.transition = "";
      const flick = Math.abs(velX) > FLICK && Math.abs(velX) > Math.abs(velY);
      if (Math.abs(dx) > w * 0.22 || flick) {
        if (dx < 0 && screen() === "hub") setScreen("machine");
        else if (dx > 0 && screen() === "machine") setScreen("hub");
      }
      paintH();
      armSwallow();
    } else if (ax === "vpage") {
      if (vt) vt.style.transition = "";
      const flick = Math.abs(velY) > FLICK && Math.abs(velY) > Math.abs(velX);
      const go = Math.abs(dy) > vh * 0.2 || flick;
      let target = go ? vIdx + (dy < 0 ? 1 : -1) : vIdx;
      target = Math.max(0, Math.min(nPanes - 1, target));
      // peinture explicite (l'état machine se met à jour de façon async via la
      // prop section → on anime tout de suite, y compris le snap-back).
      if (vt) vt.style.transform = `translateY(${-target * 100}%)`;
      commitV(target);
      armSwallow();
    } else if (ax === "vscroll" && pane) {
      // inertie : prolonge le scroll de contenu après le relâcher (momentum).
      const p = pane;
      let v = -velY * 16;
      const decay = () => {
        if (!p || Math.abs(v) < 0.4) return;
        p.scrollTop += v;
        v *= 0.92;
        requestAnimationFrame(decay);
      };
      if (Math.abs(v) > 1) requestAnimationFrame(decay);
      if (Math.abs(dy) > 6) armSwallow();
    }
    ax = null;
  }
  function cancelPointers() {
    activePointers.clear();
    dragging = false;
    ax = null;
    rootRef?.classList.remove("dragging");
  }
  // Valide une position verticale (clamp + maj de l'état réactif).
  function commitV(i: number) {
    if (screen() === "hub") {
      setHubV(Math.max(0, Math.min(2, i)));
    } else {
      const panes = machinePanes();
      const ni = Math.max(0, Math.min(panes.length - 1, i));
      props.activate(panes[ni].id);
    }
  }
  function goDot(i: number) {
    commitV(i);
  }

  return (
    <div
      class="mcockpit"
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        cancelPointers();
      }}
    >
      <div class="hpager">
        <div class="htrack">
          {/* ════════ ACCUEIL ════════ */}
          <section class="hscreen">
            <header class="m-top">
              <div class="m-title">RemoteWS</div>
              <PwaInstallButton compact label="Installer" />
            </header>
            <div class="vpager">
              <div class="vtrack">
                {/* — Connectés — */}
                <div class="vpane">
                  <div class="vhead">
                    Connectés<span class="cnt">{props.openMachines.length}</span>
                  </div>
                  <Show
                    when={props.openMachines.length > 0}
                    fallback={<div class="list-empty">Aucune machine connectée.</div>}
                  >
                    <For each={props.openMachines}>
                      {(m) => {
                        const host = () =>
                          props.clientTree
                            .flatMap((c) => c.sites.flatMap((s) => s.hosts))
                            .find((h) => h.persistentSessionCode === m.sessionCode);
                        return (
                          <div class="mcard sk" onClick={() => switchTo(m)}>
                            <span class="av sk2">{initials(m.sub.split(" › ")[0] || m.name)}</span>
                            <div class="mtxt">
                              <b>{m.name}</b>
                              <small>{m.sub}</small>
                            </div>
                            <div class="rgt">
                              {badge(host()?.online, m)}
                              <span class="chev">›</span>
                            </div>
                          </div>
                        );
                      }}
                    </For>
                  </Show>
                </div>

                {/* — Favoris — */}
                <div class="vpane">
                  <div class="vhead">★ Favoris</div>
                  <Show
                    when={props.favoriteHosts.length > 0}
                    fallback={<div class="list-empty">Aucun hôte épinglé.</div>}
                  >
                    <For each={props.favoriteHosts}>
                      {(h) => (
                        <div class="mcard sk" onClick={() => openHost(h)}>
                          <span class="av sk2">{initials(h.clientLabel || h.hostname)}</span>
                          <div class="mtxt">
                            <b>{h.displayName || h.hostname}</b>
                            <small>{hostSub(h)}</small>
                          </div>
                          <div class="rgt">
                            <button
                              class="connect"
                              onClick={(e) => {
                                e.stopPropagation();
                                openHost(h);
                              }}
                            >
                              {h.online ? "Connecter" : "Hors ligne"}
                            </button>
                          </div>
                        </div>
                      )}
                    </For>
                  </Show>
                </div>

                {/* — Tous les hosts — */}
                <div class="vpane">
                  <div class="vhead">Tous les hosts</div>
                  <Show when={props.clientTree.length > 0} fallback={<div class="list-empty">Aucun hôte visible.</div>}>
                    <For each={props.clientTree}>
                      {(c) => (
                        <>
                          <div class="lh">{c.client}</div>
                          <For each={c.sites}>
                            {(s) => (
                              <>
                                <div class="site-sub">{s.site}</div>
                                <For each={s.hosts}>
                                  {(h) => (
                                    <div class="hostrow sk2" classList={{ off: !h.online }} onClick={() => openHost(h)}>
                                      <span class="dot" classList={{ off: !h.online }} />
                                      <div class="mtxt">
                                        <b>{h.displayName || h.hostname}</b>
                                        <small>{h.online ? h.platform || "—" : "hors ligne"}</small>
                                      </div>
                                      <span
                                        class="star"
                                        classList={{ off: !props.pinnedHostIds.includes(h.hostId) }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          props.togglePin(h.hostId);
                                        }}
                                      >
                                        {props.pinnedHostIds.includes(h.hostId) ? "★" : "☆"}
                                      </span>
                                      <Show when={h.online}>
                                        <span class="chev">›</span>
                                      </Show>
                                    </div>
                                  )}
                                </For>
                              </>
                            )}
                          </For>
                        </>
                      )}
                    </For>
                  </Show>
                </div>
              </div>
              <div class="vdots" role="tablist" aria-label="Navigation du hub">
                <For each={HUB_TABS}>
                  {(t, i) => (
                    <button
                      type="button"
                      class="vdot-btn"
                      role="tab"
                      aria-selected={hubV() === i() ? "true" : "false"}
                      aria-label={t.label}
                      title={t.label}
                      data-on={hubV() === i() ? "1" : "0"}
                      onClick={() => setHubV(i())}
                    />
                  )}
                </For>
              </div>
              <Show when={hubV() < 2}>
                <button type="button" class="vchev dn" aria-label="Écran suivant" onClick={() => commitV(hubV() + 1)}>
                  ⌄
                </button>
              </Show>
            </div>
            <Show when={props.activeMachine}>
              <div class="hedge r" title="Machine" onClick={() => setScreen("machine")}>
                ›
              </div>
            </Show>
            {/* Tab-bar persistante : accès direct aux 3 écrans du hub sans dépendre du swipe. */}
            <nav class="hub-tabs" aria-label="Vues du hub">
              <For each={HUB_TABS}>
                {(t, i) => (
                  <button
                    type="button"
                    class="hub-tab"
                    aria-current={hubV() === i() ? "page" : undefined}
                    data-on={hubV() === i() ? "1" : "0"}
                    onClick={() => setHubV(i())}
                  >
                    {t.label}
                    <Show when={i() === 0}>
                      <span class="hub-tab-cnt">{props.openMachines.length}</span>
                    </Show>
                  </button>
                )}
              </For>
            </nav>
          </section>

          {/* ════════ MACHINE ════════ */}
          <section class="hscreen">
            <Show
              when={props.activeMachine}
              fallback={
                <>
                  <header class="m-top">
                    <button class="m-back" onClick={() => setScreen("hub")}>
                      ‹
                    </button>
                    <div class="m-mtitle">
                      <b>Aucune machine</b>
                      <small>reviens à l'accueil pour en connecter une</small>
                    </div>
                  </header>
                  <div class="list-empty" style={{ margin: "auto" }}>
                    Glisse vers la droite ou ‹ pour l'accueil.
                  </div>
                </>
              }
            >
              {(m) => (
                <>
                  <header class="m-top">
                    <button class="m-back" onClick={() => setScreen("hub")}>
                      ‹
                    </button>
                    <div class="m-mtitle">
                      <b>{m().name}</b>
                      <small>{m().sub}</small>
                    </div>
                    <span class="dot" classList={{ off: m().status === "off" }} />
                    <button class="m-ham" title="Outils" onClick={() => setSheetOpen(true)}>
                      ▦
                    </button>
                    <button
                      class="m-x"
                      onClick={() => {
                        void props.disconnectMachine(m().key);
                        setScreen("hub");
                      }}
                    >
                      ✕
                    </button>
                  </header>
                  <div class="vpager">
                    <div class="vtrack">
                      <For each={machinePanes()}>
                        {(p) => (
                          <Show
                            when={p.id === "info"}
                            fallback={
                              <div class="vpane tool">
                                <div class="vhead">
                                  <span class="vic">{p.kind === "control" ? "🖥" : "›"}</span>
                                  {p.label}
                                  <span class="tc" onClick={() => closeToolPane(p.id)} title={`Fermer ${p.label}`}>
                                    ✕
                                  </span>
                                </div>
                                <div class="tool-body">{props.renderToolContent(p, p.id === props.section)}</div>
                              </div>
                            }
                          >
                            <div class="vpane">
                              <div class="vhead">Info</div>
                              <MobileInfoPane
                                host={props.sessionHost}
                                sessionAgentOnline={props.effectiveHostOnline}
                                onTakeControl={(mode) => (mode === "rdp" ? openRdp() : props.onOpenControl(mode))}
                                onOpenTools={() => setSheetOpen(true)}
                              />
                            </div>
                          </Show>
                        )}
                      </For>
                    </div>
                    <div class="vdots" role="tablist" aria-label="Navigation de la machine">
                      <For each={machinePanes()}>
                        {(p, i) => (
                          <button
                            type="button"
                            class="vdot-btn"
                            role="tab"
                            aria-selected={machineVIndex() === i() ? "true" : "false"}
                            aria-label={p.label}
                            title={p.label}
                            data-on={machineVIndex() === i() ? "1" : "0"}
                            onClick={() => goDot(i())}
                          />
                        )}
                      </For>
                    </div>
                    <Show when={machineVIndex() < machinePanes().length - 1}>
                      <button
                        type="button"
                        class="vchev dn"
                        aria-label="Écran suivant"
                        onClick={() => commitV(machineVIndex() + 1)}
                      >
                        ⌄
                      </button>
                    </Show>
                  </div>
                  <div class="hedge l" title="Accueil" onClick={() => setScreen("hub")}>
                    ‹
                  </div>
                </>
              )}
            </Show>
          </section>
        </div>
      </div>

      {/* ════════ Bottom-sheet outils ════════ */}
      <div class="scrim" data-on={sheetOpen() ? "1" : "0"} onClick={() => setSheetOpen(false)} />
      <div class="sheet" data-on={sheetOpen() ? "1" : "0"}>
        <div class="sheet-grab" />
        <div class="sheet-head">
          Outils
          <button class="m-x" aria-label="Fermer les outils" onClick={() => setSheetOpen(false)}>
            ✕
          </button>
        </div>
        <div class="sheet-body">
          <Show
            when={props.toolCategories.length > 0}
            fallback={
              <div class="list-empty">
                Aucun outil disponible.
                <div class="soft" style={{ "font-size": "12px", "margin-top": "6px" }}>
                  Connectez une machine pour afficher ses outils.
                </div>
              </div>
            }
          >
            <For each={props.toolCategories}>
              {(c) => (
                <>
                  <div class="cat-h">
                    <span>{c.ic}</span>
                    {c.n}
                  </div>
                  <div class="chips">
                    <For each={c.tools}>
                      {(t) => (
                        <div
                          class="chip sk2"
                          onClick={() => {
                            props.activate(t.id);
                            setSheetOpen(false);
                          }}
                        >
                          {t.label}
                        </div>
                      )}
                    </For>
                  </div>
                </>
              )}
            </For>
          </Show>
        </div>
      </div>
    </div>
  );
}

function badge(online: boolean | undefined, m: OpenMachine): JSX.Element {
  // Consentement réel inconnu ici (par machine) — on s'appuie sur le statut.
  if (m.status === "off" || online === false) return <span class="badge ref">hors ligne</span>;
  return <span class="badge live">● live</span>;
}

// ─── Pane Info mobile (aperçu gaté consentement + identité + santé + sessions) ──
function MobileInfoPane(props: {
  host: HostData | null | undefined;
  sessionAgentOnline: boolean;
  onTakeControl: (mode?: MobileControlMode) => void;
  onOpenTools: () => void;
}): JSX.Element {
  const ws = useWs();
  const notify = useNotify();
  const confirm = useConfirm();
  const [liveData, setLiveData] = createSignal<Record<string, unknown>>({});

  onMount(() => {
    let subscribed = false;
    const subTimer = setInterval(() => {
      if (!ws.isOpen() || !ws.state.sessionCode || subscribed) return;
      try {
        ws.send(asWsOutgoing(envelope("live", "panel", "subscribe", { topic: "sysinfo", interval: 5000 })));
        subscribed = true;
      } catch {
        /* noop */
      }
    }, 1000);
    const off = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "panel", "data") &&
        (m as { payload?: { topic?: string } }).payload?.topic === "sysinfo",
      (msg) => setLiveData((msg as { payload?: { data?: Record<string, unknown> } }).payload?.data || {}),
    );
    onCleanup(() => {
      clearInterval(subTimer);
      if (subscribed && ws.isOpen()) {
        try {
          ws.send(asWsOutgoing(envelope("live", "panel", "unsubscribe", { topic: "sysinfo" })));
        } catch {
          /* noop */
        }
      }
      off();
    });
  });

  const consent = createMemo<"granted" | "pending" | "refused">(() => {
    const view = ws.state.access?.view;
    if (ws.state.supportApproved || view === "approved" || view === "not_required") return "granted";
    if (ws.state.noUserLoggedIn && ws.state.hostPolicy?.unattendedAccessAllowed === true) return "granted";
    if (view === "denied") return "refused";
    return "pending";
  });

  function num(...keys: string[]): number | null {
    const src = liveData();
    for (const k of keys) {
      const v = src[k];
      if (typeof v === "number" && Number.isFinite(v)) return v;
    }
    const stat = (props.host?.systemInfo || {}) as Record<string, unknown>;
    for (const k of keys) {
      const v = stat[k];
      if (typeof v === "number" && Number.isFinite(v)) return v;
    }
    return null;
  }
  function str(...keys: string[]): string | null {
    for (const src of [liveData(), (props.host?.systemInfo || {}) as Record<string, unknown>]) {
      for (const k of keys) {
        const v = src[k];
        if (v !== undefined && v !== null && v !== "") return String(v);
      }
    }
    return null;
  }
  const gauges = createMemo(() => {
    const cpu = num("cpuPercent", "cpu", "cpuLoadPercent");
    const mem = num("memPercent", "memoryPercent", "memoryLoadPercent");
    const disk = num("diskPercent", "systemDrivePercent");
    const down = num("netDownMbps", "rxMbps");
    const up = num("netUpMbps", "txMbps");
    return [
      { l: "CPU", v: cpu !== null ? `${Math.round(cpu)} %` : "—", p: cpu ?? 0 },
      { l: "RAM", v: mem !== null ? `${Math.round(mem)} %` : "—", p: mem ?? 0 },
      { l: "Disque", v: disk !== null ? `${Math.round(disk)} %` : "—", p: disk ?? 0 },
      {
        l: "Réseau",
        v: down !== null || up !== null ? `↓${down ?? 0} ↑${up ?? 0}` : "—",
        p: Math.min(100, ((down ?? 0) + (up ?? 0)) * 4),
      },
    ];
  });
  const sessions = createMemo(() => ws.state.rdsSessions ?? []);

  function hostCmd(action: string) {
    const hostId = props.host?.hostId;
    if (!hostId) {
      notify.warn("Action indisponible", { durationMs: 2500 });
      return;
    }
    ws.send({ type: "host:command", hostId, command: { action } } as never);
  }
  async function quickAction(a: { action: string; label: string; danger?: boolean }) {
    if (a.action === "screenshot") {
      props.onTakeControl();
      return;
    }
    if (a.danger) {
      const ok = await confirm.confirm({
        title: `${a.label} ?`,
        body: `Confirmer « ${a.label} » sur ${props.host?.displayName || props.host?.hostname || "l'hôte"} ?`,
        confirmLabel: a.label,
        level: "danger",
      });
      if (!ok) return;
    }
    hostCmd(a.action);
    notify.info(`${a.label} demandé`, { durationMs: 2200 });
  }
  const ACTIONS: { ic: string; action: string; label: string; danger?: boolean }[] = [
    { ic: "🔒", action: "lock", label: "Verrouiller" },
    { ic: "⟳", action: "restart", label: "Redémarrer", danger: true },
    { ic: "⎋", action: "logoff", label: "Fermer sess.", danger: true },
    { ic: "✉", action: "message", label: "Message" },
    { ic: "⬆", action: "elevate", label: "UAC" },
    { ic: "⎙", action: "screenshot", label: "Capture" },
  ];

  const h = () => props.host;
  return (
    <>
      {/* Aperçu gaté consentement */}
      <Show
        when={consent() === "granted"}
        fallback={
          <div class="preview sk">
            <div class="pv-bar">
              <span class="live off">{consent() === "refused" ? "✕ REFUSÉ" : "◌ EN ATTENTE"}</span>
              <span class="soft">aucun flux vidéo</span>
            </div>
            <div class="pv-body">
              <div class="lock-ic">{consent() === "refused" ? "⊘" : "🔒"}</div>
              <b>{consent() === "refused" ? "Aperçu indisponible" : "En attente du consentement"}</b>
              <small>
                {consent() === "refused"
                  ? "L'utilisateur a refusé — aucun aperçu n'est affiché."
                  : "Aucun aperçu tant que l'utilisateur n'a pas validé sur son poste."}
              </small>
              <button
                class="bigbtn acc"
                style={{ width: "auto", margin: "4px 0 0", padding: "9px 16px", "font-size": "14px" }}
                onClick={() => props.onTakeControl("ws")}
              >
                {consent() === "refused" ? "Redemander" : "Demander la prise en main"}
              </button>
            </div>
          </div>
        }
      >
        <div class="preview sk">
          <div class="pv-bar">
            <span class="live">
              <i />
              EN DIRECT
            </span>
            <span class="soft">prise en main accordée</span>
          </div>
          <div class="pv-body" onClick={() => props.onTakeControl("ws")}>
            <div class="dash mon">🖥</div>
            <b>Aperçu disponible</b>
            <small>« Prendre la main » pour le contrôle clavier / souris</small>
          </div>
        </div>
      </Show>

      <div class="mobile-control-choice">
        <button class="bigbtn acc fill sk" type="button" onClick={() => props.onTakeControl("ws")}>
          WSS
        </button>
        <button class="bigbtn acc sk" type="button" onClick={() => props.onTakeControl("rdp")}>
          RDP
        </button>
      </div>
      <Show when={consent() === "granted"}>
        <div class="bigbtn acc fill sk" onClick={() => props.onTakeControl("ws")}>
          🖥 Prendre la main
        </div>
      </Show>
      <div class="bigbtn acc sk2" onClick={() => props.onOpenTools()}>
        ▦ Ouvrir un outil
      </div>

      <div class="sec">Identité</div>
      <div class="card sk">
        <dl class="id-grid">
          <dt>OS</dt>
          <dd>{str("osVersion", "osBuild") || h()?.platform || "—"}</dd>
          <dt>IP</dt>
          <dd>{str("privateIp", "primaryIp", "localIp", "ip") || "—"}</dd>
          <dt>Agent</dt>
          <dd>{h()?.version || "—"}</dd>
          <dt>Client</dt>
          <dd>{(h()?.clientLabel || "Sans client") + " › " + (h()?.siteLabel || "—")}</dd>
        </dl>
      </div>

      <div class="sec">État santé</div>
      <div class="card sk">
        <div class="health">
          <For each={gauges()}>
            {(g) => (
              <div class="gauge">
                <div class="g-top">
                  <b>{g.l}</b>
                  <span class="v">{g.v}</span>
                </div>
                <div class="g-bar">
                  <i style={{ width: `${Math.max(0, Math.min(100, g.p))}%` }} />
                </div>
              </div>
            )}
          </For>
        </div>
      </div>

      <div class="sec">Sessions ouvertes</div>
      <div class="card sk">
        <Show
          when={sessions().length > 0}
          fallback={
            <div class="soft" style={{ "font-size": "13px" }}>
              Aucune session énumérée.
            </div>
          }
        >
          <For each={sessions()}>
            {(s) => (
              <div class="ses-row">
                <span class="av" style={{ background: s.isConsole ? "var(--dot)" : "var(--amber)" }}>
                  {(s.userName || "?").slice(0, 1).toUpperCase()}
                </span>
                <span class="who">
                  <b>{s.userName || s.sessionName || `session ${s.sessionId}`}</b>
                  <small>
                    {s.isConsole ? "Console" : "RDP"} · {s.sessionName}
                  </small>
                </span>
                <span class="st" style={{ color: s.state === "Active" ? "var(--dot)" : "var(--amber)" }}>
                  {s.state}
                </span>
              </div>
            )}
          </For>
        </Show>
      </div>

      <div class="sec">Actions rapides</div>
      <div class="card sk">
        <div class="acts">
          <For each={ACTIONS}>
            {(a) => (
              <div class="actbtn sk2" onClick={() => void quickAction(a)}>
                <span class="ai">{a.ic}</span>
                {a.label}
              </div>
            )}
          </For>
        </div>
      </div>
    </>
  );
}
